import FooterLink from '@/components/FooterLink/FooterLink';
import LocationIcon from '@/components/Icons/LocationIcon';
import MailIcon from '@/components/Icons/MailIcon';
import PhoneIcon from '@/components/Icons/PhoneIcon';
import OfficeHours from '@/components/OfficeHours/OfficeHours';
import SocialNetworkLinks from '@/components/SocialNetworkLinks/SocialNetworkLinks';
import { useAppContext } from '@/contexts/AppContext';
import { formatPhoneNumber } from '@/utils/phone';
import { useMemo } from 'react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { community, tenantClientUrl } = useAppContext();

  const links = useMemo(
    () => [
      {
        href: '/',
        children: 'Home',
      },
      {
        href: '/floor-plans',
        children: 'Floor Plans',
      },
      {
        href: '/gallery',
        children: 'Gallery',
      },
      {
        href: '/application-requirements',
        children: 'Application Requirements',
      },
      ...(tenantClientUrl
        ? [
            {
              href: `${tenantClientUrl}/user/login`,
              children: 'Residents',
              external: true,
            },
          ]
        : []),
    ],
    [tenantClientUrl],
  );

  if (!community) {
    return null;
  }

  return (
    <footer className="bg-primary-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1">
            <h3 className="text-accent-500 text-2xl font-heading font-bold mb-4">
              {community.name}
            </h3>
            <p className="text-sm mb-4">
              Experience modern living in our beautiful build-to-rent community. Your perfect home
              awaits.
            </p>
            <SocialNetworkLinks />
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {links.map((link) => (
                <li key={`footer-link-${link.children}`}>
                  <FooterLink href={link.href} external={link.external}>
                    {link.children}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-2 text-sm">
              {community.address ? (
                <li className="flex items-start">
                  <LocationIcon />
                  {community.address.addr1}
                  {community.address.addr2 ? ' ' + community.address.addr2 : ''}
                  <br />
                  {community.address.city}, {community.address.state} {community.address.postalCode}
                </li>
              ) : null}
              {community.contactInformation?.phone ? (
                <li className="flex items-center">
                  <PhoneIcon />
                  Call or Text: {formatPhoneNumber(community.contactInformation.phone)}
                </li>
              ) : null}
              {community.contactInformation?.email ? (
                <li className="flex items-center">
                  <MailIcon />
                  {community.contactInformation.email}
                </li>
              ) : null}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4">Office Hours</h4>
            <OfficeHours />
          </div>
        </div>

        <div className="border-t border-primary-700 mt-8 pt-8 text-sm text-center">
          <p>
            &copy; {currentYear} {community.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
